import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Gestión de comunidad — bespoke sections (incl. membresías y suscripciones).
// ===========================================================================

export function ComunidadDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Gestión de comunidad"
      subtitle="Residentes, unidades, roles, membresías y suscripciones"
      icon="ri-community-line"
      stats={[
        { label: "Residentes / miembros", value: "1,204", icon: "ri-group-line", change: "+18", positive: true },
        { label: "Unidades", value: "486", icon: "ri-home-4-line" },
        { label: "Membresías activas", value: "312", icon: "ri-vip-crown-line" },
        { label: "Guardias en turno", value: "7", icon: "ri-shield-user-line" },
      ]}
      chart={{
        title: "Altas de residentes (6 meses)",
        data: [
          { name: "Ene", value: 22 }, { name: "Feb", value: 18 }, { name: "Mar", value: 31 },
          { name: "Abr", value: 26 }, { name: "May", value: 35 }, { name: "Jun", value: 18 },
        ],
      }}
      recent={{
        title: "Actividad reciente",
        items: [
          { primary: "Alta de residente — Depto 712", secondary: "Hace 1 h", badge: "Nuevo", tone: "ok" },
          { primary: "Membresía renovada — Gold", secondary: "Hace 2 h", badge: "Membresía", tone: "info" },
          { primary: "Cambio de turno — Caseta", secondary: "Hace 3 h", badge: "Turno", tone: "warn" },
        ],
      }}
    />
  );
}

// --- Directorio ------------------------------------------------------------
interface Persona extends Record<string, unknown> {
  id: string; nombre: string; tipo: string; unidad: string; contacto: string; estado: string;
}
const personaColumns: CrudColumn<Persona>[] = [
  { key: "nombre", label: "Nombre" },
  { key: "tipo", label: "Tipo" },
  { key: "unidad", label: "Unidad" },
  { key: "contacto", label: "Contacto" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const personaFields: CrudField[] = [
  { key: "nombre", label: "Nombre completo", required: true, placeholder: "Nombre y apellidos" },
  { key: "tipo", label: "Tipo", type: "select", defaultValue: "Residente", options: [
    { value: "Residente", label: "Residente" }, { value: "Miembro", label: "Miembro" }, { value: "Propietario", label: "Propietario" },
  ] },
  { key: "unidad", label: "Unidad", required: true, placeholder: "Depto 402 / Casa 12" },
  { key: "contacto", label: "Contacto", type: "email", placeholder: "correo o teléfono" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" },
  ] },
];
const personaSeed: Persona[] = [
  { id: "u1", nombre: "Juan Pérez", tipo: "Residente", unidad: "Depto 402", contacto: "juan@mail.com", estado: "Activo" },
  { id: "u2", nombre: "Ana López", tipo: "Propietario", unidad: "Casa 12", contacto: "55 1234 5678", estado: "Activo" },
  { id: "u3", nombre: "Carlos Ruiz", tipo: "Miembro", unidad: "—", contacto: "carlos@mail.com", estado: "Inactivo" },
];
export const DirectorioSection = () => (
  <CrudSection<Persona> title="Directorio" subtitle="Residentes y miembros de la comunidad"
    icon="ri-contacts-book-line" columns={personaColumns} fields={personaFields} seed={personaSeed} searchKey="nombre" />
);

// --- Unidades y espacios ---------------------------------------------------
interface Unidad extends Record<string, unknown> {
  id: string; identificador: string; tipo: string; torre: string; ocupacion: string;
}
const unidadColumns: CrudColumn<Unidad>[] = [
  { key: "identificador", label: "Unidad" },
  { key: "tipo", label: "Tipo" },
  { key: "torre", label: "Torre / Zona" },
  { key: "ocupacion", label: "Ocupación", align: "center", render: (r) => <StatusTextBadge value={r.ocupacion} /> },
];
const unidadFields: CrudField[] = [
  { key: "identificador", label: "Identificador", required: true, placeholder: "Depto 402" },
  { key: "tipo", label: "Tipo", type: "select", defaultValue: "Departamento", options: [
    { value: "Departamento", label: "Departamento" }, { value: "Casa", label: "Casa" }, { value: "Local", label: "Local" }, { value: "Espacio", label: "Espacio" },
  ] },
  { key: "torre", label: "Torre / Zona", placeholder: "Torre A" },
  { key: "ocupacion", label: "Ocupación", type: "select", defaultValue: "Ocupada", options: [
    { value: "Ocupada", label: "Ocupada" }, { value: "Disponible", label: "Disponible" },
  ] },
];
const unidadSeed: Unidad[] = [
  { id: "un1", identificador: "Depto 402", tipo: "Departamento", torre: "Torre A", ocupacion: "Ocupada" },
  { id: "un2", identificador: "Casa 12", tipo: "Casa", torre: "Zona norte", ocupacion: "Ocupada" },
  { id: "un3", identificador: "Local 3", tipo: "Local", torre: "Plaza", ocupacion: "Disponible" },
];
export const UnidadesSection = () => (
  <CrudSection<Unidad> title="Unidades y espacios" subtitle="Casas, departamentos, locales y espacios"
    icon="ri-home-4-line" columns={unidadColumns} fields={unidadFields} seed={unidadSeed} searchKey="identificador" />
);

// --- Roles y permisos ------------------------------------------------------
interface Rol extends Record<string, unknown> {
  id: string; rol: string; alcance: string; usuarios: string; estado: string;
}
const rolColumns: CrudColumn<Rol>[] = [
  { key: "rol", label: "Rol" },
  { key: "alcance", label: "Alcance" },
  { key: "usuarios", label: "Usuarios" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const rolFields: CrudField[] = [
  { key: "rol", label: "Nombre del rol", required: true, placeholder: "Administrador" },
  { key: "alcance", label: "Alcance", required: true, placeholder: "Total / Operativo / Lectura" },
  { key: "usuarios", label: "Usuarios", type: "number", defaultValue: 0 },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" },
  ] },
];
const rolSeed: Rol[] = [
  { id: "ro1", rol: "Administrador", alcance: "Total", usuarios: "3", estado: "Activo" },
  { id: "ro2", rol: "Guardia", alcance: "Operativo", usuarios: "9", estado: "Activo" },
  { id: "ro3", rol: "Residente", alcance: "Lectura", usuarios: "1192", estado: "Activo" },
];
export const RolesSection = () => (
  <CrudSection<Rol> title="Roles y permisos" subtitle="Roles y niveles de acceso"
    icon="ri-shield-user-line" columns={rolColumns} fields={rolFields} seed={rolSeed} searchKey="rol" />
);

// --- Guardias y turnos -----------------------------------------------------
interface Guardia extends Record<string, unknown> {
  id: string; nombre: string; puesto: string; turno: string; estado: string;
}
const guardiaColumns: CrudColumn<Guardia>[] = [
  { key: "nombre", label: "Guardia" },
  { key: "puesto", label: "Puesto" },
  { key: "turno", label: "Turno" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const guardiaFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "M. Soto" },
  { key: "puesto", label: "Puesto", required: true, placeholder: "Caseta principal" },
  { key: "turno", label: "Turno", type: "select", defaultValue: "Matutino", options: [
    { value: "Matutino", label: "Matutino" }, { value: "Vespertino", label: "Vespertino" }, { value: "Nocturno", label: "Nocturno" },
  ] },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Descanso", label: "Descanso" },
  ] },
];
const guardiaSeed: Guardia[] = [
  { id: "g1", nombre: "M. Soto", puesto: "Caseta principal", turno: "Nocturno", estado: "Activo" },
  { id: "g2", nombre: "L. Reyes", puesto: "Torres A-B", turno: "Matutino", estado: "Activo" },
  { id: "g3", nombre: "J. Mora", puesto: "Amenidades", turno: "Vespertino", estado: "Descanso" },
];
export const GuardiasSection = () => (
  <CrudSection<Guardia> title="Guardias y turnos" subtitle="Personal de seguridad y sus turnos"
    icon="ri-user-star-line" columns={guardiaColumns} fields={guardiaFields} seed={guardiaSeed} searchKey="nombre" />
);

// --- Membresías ------------------------------------------------------------
interface Membresia extends Record<string, unknown> {
  id: string; miembro: string; plan: string; vigencia: string; estado: string;
}
const membresiaColumns: CrudColumn<Membresia>[] = [
  { key: "miembro", label: "Miembro" },
  { key: "plan", label: "Plan" },
  { key: "vigencia", label: "Vigencia" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const membresiaFields: CrudField[] = [
  { key: "miembro", label: "Miembro", required: true, placeholder: "Nombre / unidad" },
  { key: "plan", label: "Plan", type: "select", required: true, options: [
    { value: "Básico", label: "Básico" }, { value: "Gold", label: "Gold" }, { value: "Premium", label: "Premium" },
  ] },
  { key: "vigencia", label: "Vigencia", type: "date", required: true },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Por vencer", label: "Por vencer" }, { value: "Vencido", label: "Vencido" },
  ] },
];
const membresiaSeed: Membresia[] = [
  { id: "m1", miembro: "Ana López", plan: "Gold", vigencia: "2026-12-31", estado: "Activo" },
  { id: "m2", miembro: "Depto 402", plan: "Premium", vigencia: "2026-07-15", estado: "Por vencer" },
  { id: "m3", miembro: "Carlos Ruiz", plan: "Básico", vigencia: "2026-05-01", estado: "Vencido" },
];
export const MembresiasSection = () => (
  <CrudSection<Membresia> title="Membresías" subtitle="Planes, beneficios y vigencias"
    icon="ri-vip-crown-line" columns={membresiaColumns} fields={membresiaFields} seed={membresiaSeed} searchKey="miembro" />
);

// --- Suscripciones (log) ---------------------------------------------------
interface Suscripcion extends Record<string, unknown> {
  id: string; concepto: string; miembro: string; monto: string; periodicidad: string; estado: string;
}
const suscripcionColumns: CrudColumn<Suscripcion>[] = [
  { key: "concepto", label: "Concepto" },
  { key: "miembro", label: "Miembro" },
  { key: "monto", label: "Monto", align: "right" },
  { key: "periodicidad", label: "Periodicidad" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const suscripcionSeed: Suscripcion[] = [
  { id: "s1", concepto: "Membresía Gold", miembro: "Ana López", monto: "$1,200", periodicidad: "Mensual", estado: "Pagado" },
  { id: "s2", concepto: "Estacionamiento extra", miembro: "Depto 402", monto: "$450", periodicidad: "Mensual", estado: "Pendiente" },
  { id: "s3", concepto: "Membresía Premium", miembro: "Casa 12", monto: "$2,400", periodicidad: "Anual", estado: "Pagado" },
];
export const SuscripcionesSection = () => (
  <CrudSection<Suscripcion> title="Suscripciones" subtitle="Cobros recurrentes de la comunidad"
    icon="ri-refresh-line" columns={suscripcionColumns} fields={[]} seed={suscripcionSeed} searchKey="miembro" readOnly />
);
